import AccidentalDeath from "#src/types/quote-params/AccidentalDeath.ts";
import FinalExpense from "#src/types/quote-params/FinalExpense.ts";
import {
  AccumulationIUL,
  AllUniversalLife,
  ProtectionIUL,
  ProtectionVUL,
  UniversalLife,
} from "#src/types/quote-params/UniversalLife.ts";
import LinkedBenefit from "#src/types/quote-params/LinkedBenefit.ts";
import LongTermCare from "#src/types/quote-params/LongTermCare.ts";
import ReturnOfPremium from "#src/types/quote-params/ReturnOfPremium.ts";
import { Term, NonMedQuoteParams } from "#src/types/quote-params/Term.ts";
import WholeLife from "#src/types/quote-params/WholeLife.ts";
import IncomeRider from "#src/types/quote-params/IncomeRider.ts";
import {
  type InsuredData,
  type ModeDataItem,
  type ParsingData,
  type QuoteRequestParams,
  type RenderRules,
  type QuoteParams,
} from "#src/types/QuoteParams.ts";
import { PRODUCTS, UL_TYPE } from "#src/constants/products.ts";
import { CONCEPTS } from "#src/constants/concepts.ts";

export const CATEGORIES = {
  GUARANTEED_ISSUE: "guaranteed_issue",
  GUARANTEED_ISSUE_FOR_BUILD: "guaranteed_issue_for_build",
};

export type Product = (typeof PRODUCTS)[keyof typeof PRODUCTS];
export type Category = (typeof CATEGORIES)[keyof typeof CATEGORIES];
export type Concept = (typeof CONCEPTS)[keyof typeof CONCEPTS];
export type UlType = (typeof UL_TYPE)[keyof typeof UL_TYPE];

export function categoryToConstantProduct(str: string) {
  if (!str?.indexOf) return null;
  const rawCategory = str.split(".");
  const [base] = rawCategory;
  const categories = [];
  if (base === "whole_life") {
    if (rawCategory.includes("none")) {
      categories.push(PRODUCTS.WHOLE_LIFE);
    }
    if (rawCategory.includes("final_expense")) {
      categories.push(PRODUCTS.FINAL_EXPENSE);
    }
  }

  if (base === "universal_life") {
    if (rawCategory.includes("variable")) {
      categories.push(PRODUCTS.PROTECTION_VUL);
    }
    if (rawCategory.includes("accumulation")) {
      categories.push(PRODUCTS.ACCUMULATION_IUL);
    }
    if (rawCategory.includes("protection")) {
      categories.push(PRODUCTS.PROTECTION_IUL);
    }
    if (rawCategory.includes("none")) {
      categories.push(PRODUCTS.UNIVERSAL_LIFE);
    }
    if (rawCategory.includes("linked_benefit")) {
      categories.push(PRODUCTS.LINKED_BENEFIT);
    }
  }

  if (base === "term") {
    if (rawCategory.includes("none")) {
      categories.push(PRODUCTS.TERM);
    }
    if (rawCategory.includes("return_of_premium")) {
      categories.push(PRODUCTS.ROP_TERM);
    }
    if (rawCategory.includes("non_med")) {
      categories.push(PRODUCTS.NON_MED);
    }
    if (rawCategory.includes("ul_term")) {
      categories.push(PRODUCTS.UNIVERSAL_LIFE);
    }
  }

  if (base === "accidental_death") {
    if (rawCategory.includes("accidental_death")) {
      categories.push(PRODUCTS.ACCIDENTAL_DEATH);
    }
  }

  if (base === "annuity") {
    if (rawCategory.includes("income_rider")) {
      categories.push(PRODUCTS.INCOME_RIDER);
    }
  }

  if (base === "ltc") {
    categories.push(PRODUCTS.LTC);
  }

  return categories;
}

export function ProductType(type: Product | Concept): ProductTypeInfo {
  return {
    [PRODUCTS.INCOME_RIDER]: IncomeRider(),
    [PRODUCTS.FINAL_EXPENSE]: FinalExpense(),
    [PRODUCTS.ACCIDENTAL_DEATH]: AccidentalDeath(),
    [PRODUCTS.ACCUMULATION_IUL]: AccumulationIUL(),
    [PRODUCTS.PROTECTION_VUL]: ProtectionVUL(),
    [PRODUCTS.PROTECTION_IUL]: ProtectionIUL(),
    [PRODUCTS.UNIVERSAL_LIFE]: UniversalLife(),
    [PRODUCTS.LTC]: LongTermCare(),
    [PRODUCTS.ROP_TERM]: ReturnOfPremium(),
    [PRODUCTS.TERM]: Term(),
    [PRODUCTS.NON_MED]: NonMedQuoteParams(),
    [PRODUCTS.WHOLE_LIFE]: WholeLife(),
    [PRODUCTS.LINKED_BENEFIT]: LinkedBenefit(),
    [CONCEPTS.ALL_UL]: AllUniversalLife(),
  }[type];
}

export function getProductType(params: QuoteParams) {
  let type = params.selected_type;
  if (type === CONCEPTS.ALL_UL) {
    type = params.subtype || type;
  }
  if (!type) throw "Missing Product Type";

  const productType = ProductType(type);
  return productType;
}

export interface ProductTypeInfo {
  type: Product | Concept;
  display: string;
  defaults: Partial<QuoteRequestParams>;
  requestCategory: string;
  renderRules: (
    params: QuoteParams,
    extraData?: { conversion: boolean }
  ) => RenderRules;
  eAppParser: (args: ParsingData) => Partial<QuoteParams>; // unsure how to type for now
  toQuoteRequest: (args: QuoteRequestData) => Partial<QuoteRequestParams>;
  modes: ModeDataItem[];
};

export interface QuoteRequestData {
  params: QuoteParams;
  insured: InsuredData;
  otherInsured?: InsuredData;
  agentId: number;
  conversion?: boolean;
};
